import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import DefaultModal from 'components/Utils/DefaultModal';
import { TopBar } from 'components/Patient/CPP/CPPInput/Inputs/CPPInputCommonViews';
import EditFavorites from 'components/HealthRecords/Documents/DocumentTemplate/DocumentTemplateFavorites/EditFavorites';
import DocumentTemplateList from 'components/HealthRecords/Documents/DocumentTemplateList';
import DefaultButton from 'components/Utils/NewDefaultButton';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { PrefilledTemplates } from 'components/HealthRecords/Documents/DocumentTemplate/PrefilledTemplates';
import { DefaultSwitch } from 'services/UIUtils';
import { useIsLoading } from 'careand-redux/slices/ui/hooks';
import FavoritesTagManager from 'components/HealthRecords/Prescriptions/FavoritesTagManager';

const DocumentTemplateFavorites = (props) => {
  const dispatch = useAppDispatch();

  const authentication = useAppSelector((state) => state.authentication);
  const templates = useAppSelector((state) => state.templates.templates);
  const templateCategories = useAppSelector((state) => state.templates.templateCategories);
  const physicians = useAppSelector((state) => state.physicians.byId);

  const [showEditModal, setShowEditModal] = useState(false);
  const [savingFavorites, setSavingFavorites] = useState(false);
  const [categoryIndex, setCategoryIndex] = useState(0);
  const [showDrugManager, setShowDrugManager] = useState(false);

  const physician = physicians[authentication.user.id] || authentication.user;

  const favorites = physician.favorite_templates || [];

  async function onSaveFavorites(updatedFavorites) {
    setShowEditModal(false);
    setSavingFavorites(true);

    await dispatch(
      api.Physicians.updatePhysician({
        id: physician.id,
        physician: { ...physician, favorite_templates: updatedFavorites },
      }),
      {
        title: 'Favorites',
        successText: 'Favorites have been updated successfully!',
      },
    );
    setSavingFavorites(false);
  }

  return (
    <>
      {!!props.onClose && <TopBar title="Favorites" onClose={props.onClose} />}
      <div className="tabs">
        <Tabs
          value={categoryIndex}
          indicatorColor="primary"
          textColor="primary"
          onChange={(event, index) => setCategoryIndex(index)}
        >
          {['Templates', 'Prefilled Templates', 'Drugs'].map((name) => {
            return <Tab key={name} label={name} />;
          })}
        </Tabs>
      </div>
      {showEditModal && (
        <DefaultModal
          className="edit-favorites-modal"
          title="Edit Favorites"
          wrap={true}
          padding={false}
          content={
            <EditFavorites
              templates={templates}
              templateCategories={templateCategories}
              favorites={favorites}
              onSave={onSaveFavorites}
              onClose={() => setShowEditModal(false)}
            />
          }
          isOpen={showEditModal}
          onRequestClose={() => setShowEditModal(false)}
        />
      )}
      {categoryIndex === 0 && (
        <div className="details-panel new-diagnostic">
          <DocumentTemplateList
            hideAddButton
            ids={favorites}
            openCategories={true}
            startExpanded={true}
            hideTitle={true}
            patient={props.patient}
          />
          <div className="button-wrapper mt-2">
            <DefaultButton
              name="Edit Favorites"
              icon="star"
              style={{ width: '200px' }}
              loading={savingFavorites}
              onClick={() => setShowEditModal(true)}
            />
          </div>
        </div>
      )}
      {categoryIndex === 1 && (
        <ManageFavoritePrefilledTemplates physician={physician} patient={props.patient} />
      )}
      {categoryIndex === 2 && (
        <div className="p-5">
          <DefaultButton
            name="Manage Favorite Drugs"
            icon="star"
            onClick={() => setShowDrugManager(true)}
            className="blue"
            style={{ width: '220px' }}
          />
        </div>
      )}
      {showDrugManager && (
        <FavoritesTagManager
          isOpen={showDrugManager}
          onRequestClose={() => setShowDrugManager(false)}
        />
      )}
    </>
  );
};

const ManageFavoritePrefilledTemplates = ({ physician, patient }) => {
  const dispatch = useAppDispatch();
  const [edit, setEdit] = useState(false);
  const [selected, setSelected] = useState([]);
  const saving = useIsLoading(api.Physicians.updatePhysician);

  useEffect(() => {
    setSelected(physician.favorite_prefilled ?? []);
  }, [physician]);

  const onSave = useCallback(async () => {
    await dispatch(
      api.Physicians.updatePhysician({
        id: physician.id,
        physician: { ...physician, favorite_prefilled: selected },
      }),
    );
    setEdit(false);
  }, [physician, selected]);

  const toggle = useCallback((id, value) => {
    if (value === undefined) {
      setSelected((prev) => {
        const idx = prev.indexOf(id);
        if (idx === -1) {
          return [...prev, id];
        }
        return prev.filter((it) => it !== id);
      });
    } else {
      setSelected((prev) => (value ? [...prev, id] : prev.filter((it) => it !== id)));
    }
  }, []);

  const renderTemplateToggle = useCallback(
    (template) => {
      return (
        <DefaultSwitch
          name="prefilled"
          onChange={(event, value) => {
            toggle(template.id, value);
          }}
          reversed={true}
          padded={false}
          checked={selected.includes(template.id)}
        />
      );
    },
    [selected],
  );

  return (
    <div className="p-5 overflow-auto">
      <PrefilledTemplates
        physician={physician}
        patient={patient}
        showOnly={!edit ? physician.favorite_prefilled ?? [] : undefined}
        hideCreateButton
        hideActionButtons={edit}
        extraItemButton={edit ? renderTemplateToggle : null}
        onClick={edit ? () => {} : undefined}
      />
      {!edit && <DefaultButton icon="star" name="Edit Favorites" onClick={() => setEdit(true)} />}
      {edit && <DefaultButton name="Save Favorites" onClick={onSave} loading={saving} />}
    </div>
  );
};

DocumentTemplateFavorites.propTypes = {
  patient: PropTypes.object.isRequired,
  onClose: PropTypes.func,
};

export default DocumentTemplateFavorites;
